<template>
  <div class="exchange-height flex-between flex-col">
    <Teleport v-if="mounted" to="#header-breadcrumbs">
      <CBreadcrumb v-bind="{ routes }" disableGoToBack />
    </Teleport>
    <CCard :class="[paginationData?.total > 5 ? 'p-5' : 'px-5 pt-5 pb-2']">
      <CTableWrapper
        :title="t('count_exchanges', { count: paginationData?.total })"
        :data="tableData"
        :head="exchangeHead"
        :items-per-page="paginationData?.defaultLimit ?? 0"
        :limit="paginationData?.defaultLimit ?? 0"
        :current-page="paginationData?.currentPage ?? 0"
        :total="paginationData?.total"
        @search="onSearch"
        body-tr-class="hover:bg-transparent !cursor-default"
        td-class="last:pr-6"
        @itemsPerPage="onChangeLimit"
        :loading="loading"
        @pageChange="onPageChange"
        th-class="!text-left"
      >
        <template #id="{ row: data }">
          {{ data?._index }}
        </template>
        <template #name="{ row: data }">
          <span class="text-sm leading-130 text-dark-300 font-semibold">{{
            data?.title
          }}</span>
        </template>
        <template #exchange="{ row: data }">
          <span class="capitalize">{{ data?.type }}</span>
        </template>
        <template #key="{ row: data }">
          {{
            convertToStars(data?.key) ? convertToStars(data?.key) : "*******"
          }}
        </template>
        <template #secret="{ row: data }">
          {{
            convertToStars(data?.secret)
              ? convertToStars(data?.secret)
              : "********"
          }}
        </template>
        <template #afterSearch>
          <CButton
            :text="$t('add_exchange')"
            class="max-w-full mt-3 md:mt-0 md:max-w-[150px]"
            icon="icon-add !text-lg !leading-130"
            icon-position="left"
            size="sm"
            @click="isUserVerified(checkIsActivePlan, false)"
          />
        </template>
        <template #no-data>
          <CNodata
            main-class="min-h-[297px]"
            image="/images/svg/no-data/no-exchanges.svg"
            :title="$t('no_exchanges')"
            :subtitle="$t('no_exchanges_text')"
            :button-text="$t('add_exchange')"
            @submit="isUserVerified(checkIsActivePlan, false)"
          />
        </template>
        <template #action="{ row: data }">
          <CActionsDropdown
            :list="exchangeActions"
            :selected-item="data"
            :disabled="data?.has_running_bot"
            @edit="(item) => showExchangeModal(item)"
            @delete="(item) => showExchangeDelete(item)"
            custome-class="!-top-14 !right-10"
          />
        </template>
      </CTableWrapper>
    </CCard>

    <CExchangeForm
      type="add"
      :show="addModal"
      title="add_exchange"
      v-bind="{ form }"
      :loading="isLoading"
      @close="addModalClose"
      @submit="addExchange"
    />
    <CExchangeForm
      type="edit"
      :show="editModal"
      title="edit_exchange"
      v-bind="{ form }"
      :loading="isLoading"
      @close="editModalClose"
      @submit="editExchange"
      custom-dialog-class="!flex-col max-w-[532px]"
    >
      <template #footer>
        <div class="bg-white rounded-xl p-5">
          <i18n-t
            tag="h4"
            keypath="trusted_ips_recommends"
            class="text-sm text-dark leading-normal"
          >
            <template #recommends>
              <span class="text-gray-50">({{ $t("recommends") }})</span>
            </template>
          </i18n-t>
          <p
            class="text-sm leading-130 text-dark font-normal p-3 my-2 bg-gray-100 rounded-lg"
          >
            3.28.220.124 3.28.26.227 13.246.125.169 13.247.0.60 13.213.226.131
            13.215.255.12 13.51.147.158 16.170.180.194 52.60.236.132 3.96.75.139
          </p>
          <p
            class="flex items-start gap-2.5 p-2 rounded-lg bg-gray-300 text-blue-100 text-sm leading-normal"
          >
            <i class="icon-fill-info text-2xl leading-6 text-blue-100" />
            {{ $t("trusted_ips_text") }}
          </p>
        </div>
      </template>
    </CExchangeForm>
    <div
      class="info mt-8 overflow-hidden relative flex-y-start flex-col gap-4 px-6 py-5 rounded-xl w-full max-w-[555px]"
    >
      <div class="relative z-10 flex items-center mb-4">
        <i class="icon-fill-info text-3xl text-green mr-2"></i>
        <h5 class="text-2xl text-white font-semibold">
          {{ $t("how_to_working") }}
        </h5>
      </div>
      <RouterLink to="/guide?tab=3&child=4" class="w-full flex max-w-[140px]">
        <CButton
          class="info-btn"
          :text="$t('instructions')"
          :icon="'icon-chevron-curved -rotate-90'"
        />
      </RouterLink>
      <img
        class="absolute right-0 bottom-0"
        src="/images/learning.svg"
        alt="Learning image"
      />
    </div>
  </div>
  <CDeleteDialog
    :show="showDelete"
    variant="warning"
    :title="$t('bot_delete')"
    :subtitle="$t('bot_delete_text')"
    :loading="isLoading"
    @close="showDelete = false"
    @submit="deleteExchange(selectedExchange)"
  />
  <CPlanDialog :show="showModal" @close="showModal = false" />
</template>

<script setup lang="ts">
import CPlanDialog from "@/components/Common/Dialog/CPlanDialog.vue";

import CTableWrapper from "@/components/Common/Table/CTableWrapper.vue";
import CButton from "@/components/Common/CButton.vue";
import CCard from "@/components/Card/CardWrapper.vue";
import { exchangeActions, exchangeHead } from "@/modules/Exchanges/data";
import { convertToStars } from "@/utils";
import CActionsDropdown from "@/components/Common/Dropdown/CActionsDropdown.vue";
import CNodata from "@/components/Common/CNodata.vue";
import CExchangeForm from "@/modules/Exchanges/components/Dialog/CExchangeForm.vue";
import { computed, ref, watch, onMounted } from "vue";
import { useForm } from "@/composables/useForm";
import { required, requiredIf } from "@vuelidate/validators";
import { useTableFetch } from "@/composables/useTableFetch";
import { useI18n } from "vue-i18n";
import ApiService from "@/services/ApiService";
import { useCustomToast } from "@/composables/useCustomToast";
import { useAuthStore } from "@/modules/Auth/stores";
import CDeleteDialog from "@/components/Common/Dialog/CDeleteDialog.vue";
import router from "@/router";
import CBreadcrumb from "@/components/Common/CBreadcrumb.vue";
import { useMounted } from "@/composables/useMounted";
import { isUserVerified } from "@/composables/useUserVerified";
import { trackCustomEvent } from "@/utils/fbq";

import { usePushToDataLayer } from "@/composables/usePushToDataLayer";
const { pushToDataLayer } = usePushToDataLayer();

const { t } = useI18n();
const { showToast } = useCustomToast();
const exchanges = ref();
const user = computed(() => useAuthStore().user);

const showModal = ref(false);

const {
  tableData,
  paginationData,
  onSearch,
  onPageChange,
  onChangeLimit,
  loading,
  fetchTableData,
} = useTableFetch("/bot/exchange/");
onMounted(() => {
  fetchTableData();
  getExchanges();
});

const form = useForm(
  {
    title: "",
    type: "",
    api_key: "",
    secret_key: "",
    password: "",
  },
  {
    title: { required },
    type: { requiredIf: requiredIf(() => !editModal.value) },
    api_key: { requiredIf: requiredIf(() => !editModal.value) },
    secret_key: { requiredIf: requiredIf(() => !editModal.value) },
    password: { requiredIf: requiredIf(() => form.values.type === "kucoin") },
  },
);

const addModal = ref(false);
const isLoading = ref(false);
function addExchange() {
  form.$v.value.$touch();
  if (!form.$v.value.$invalid) {
    isLoading.value = true;
    pushToDataLayer("create_exchange_attempt", {
      event_category: "create_exchange",
      event_label: "create_exchange_attempt",
      button_text: "Create Exchange",
    });
    const postData = {
      ...form.values,
    };

    if (form.values.type !== "kucoin") {
      delete postData.password;
    }
    ApiService.post("/bot/exchange/add/", postData)
      .then(() => {
        showToast(t("successfully_added"), "success");
        fetchTableData();
        clearFormAfterSend();
        pushToDataLayer("create_exchange_success", {
          event_category: "create_exchange",
          event_label: "create_exchange_success",
          button_text: "Create Exchange",
        });
        trackCustomEvent("UserAddExchangeSuccess", {
          event_category: "form",
          event_label: "add_exchange_success",
          button_text: "Add Exchange",
        });
      })
      .catch((err: any) => {
        if (
          err?.response?.status === 400 ||
          err?.response?.data?.key === "exchange_key_not_valid"
        ) {
          showToast(t("key_not_valid"), "error");
        } else {
          showToast(t("some_error"), "error");
        }
      })
      .finally(() => {
        addModal.value = false;
        isLoading.value = false;
      });
  }
}

const showDelete = ref(false);
const selectedExchange = ref();
const { mounted } = useMounted();

function showExchangeDelete(exchange: any) {
  const planTitle = user.value?.plan;
  // const planLeftDays = user.value?.plan_left_days;

  if (planTitle !== null && planTitle !== "") {
    showDelete.value = true;
    selectedExchange.value = exchange;
  } else {
    showModal.value = true;
  }
}

function deleteExchange(exchange: any) {
  isLoading.value = true;
  ApiService.delete(`/bot/exchange/${exchange?.id}/`)
    .then(() => {
      showToast(t("successfully_deleted"), "success");
      fetchTableData();
      pushToDataLayer("delete_exchange_success", {
        event_category: "delete_exchange",
        event_label: "delete_exchange_success",
        button_text: "Delete Exchange",
      });
      trackCustomEvent("UserDeleteExchangeSuccess", {
        event_category: "form",
        event_label: "delete_exchange_success",
        button_text: "Delete Exchange",
      });
    })
    .catch(() => {
      showToast(t("some_error"), "error");
    })
    .finally(() => {
      showDelete.value = false;
      selectedExchange.value = null;
      isLoading.value = false;
    });
}

const editModal = ref(false);

function editExchange() {
  form.$v.value.$touch();
  if (!form.$v.value.$invalid) {
    isLoading.value = true;
    ApiService.put(`/bot/exchange/${selectedExchange?.value?.id}/`, {
      user: user?.value?.id,
      ...form.values,
    })
      .then(() => {
        showToast(t("successfully_edit"), "success");
        fetchTableData();
        pushToDataLayer("edit_exchange_success", {
          event_category: "edit_exchange",
          event_label: "edit_exchange_success",
          button_text: "Edit Exchange",
        });
        trackCustomEvent("UserEditExchangeSuccess", {
          event_category: "form",
          event_label: "edit_exchange_success",
          button_text: "Edit Exchange",
        });
      })
      .catch((err: any) => {
        if (
          err?.response?.status === 400 ||
          err?.response?.data[0] === "Binance API keys are not valid"
        ) {
          router.push("/guide?tab=instructions");
          showToast(t("key_not_valid"), "error");
        } else {
          showToast(t("some_error"), "error");
        }
      })
      .finally(() => {
        editModal.value = false;
        selectedExchange.value = null;
        isLoading.value = false;
        clearFormAfterSend();
      });
  }
}

function getExchanges() {
  ApiService.get("/bot/exchange/").then((res) => {
    exchanges.value = res.data?.results;
  });
}

function addModalClose() {
  addModal.value = false;
  clearFormAfterSend();
}

function editModalClose() {
  editModal.value = false;
  clearFormAfterSend();
}

function clearFormAfterSend() {
  form.values.title = "";
  form.values.type = "";
  form.values.api_key = "";
  form.values.secret_key = "";
  form.$v.value.$reset();
}

function showExchangeModal(exchange: any) {
  const planTitle = user.value?.plan;

  if (planTitle !== null && planTitle !== "") {
    editModal.value = true;
    selectedExchange.value = exchange;
    form.values.title = exchange?.title;
    form.values.type = exchange?.type;
    form.values.api_key = exchange?.api_key;
    form.values.secret_key = exchange?.secret_key;
  } else {
    showModal.value = true;
  }
}

watch(
  () => exchanges.value,
  (val) => {
    form.values.title = val?.title;
    form.values.type = val?.user;
  },
  {
    immediate: true,
  },
);

const checkIsActivePlan = () => {
  const planTitle = user.value?.plan;
  // const planLeftDays = user.value?.plan_left_days;

  if (planTitle !== null && planTitle !== "") {
    addModal.value = true;
  } else {
    showModal.value = true;
  }
};

const routes = computed(() => [
  {
    name: t("exchanges"),
    route: "/exchanges",
  },
]);

onMounted(() => {
  pushToDataLayer("pageview", {
    page: "exchanges",
    event_category: "page",
    event_label: "exchanges_page_view",
    button_text: "None",
  });
});
</script>
<style lang="scss">
.exchange-height {
  min-height: calc(100vh - 110px);
}
.info {
  border: 1px solid rgba(22, 204, 83, 0.3);
  background: linear-gradient(180deg, #000 -9.68%, #083717 88.33%);
  box-shadow: 0px 0px 30px 0px rgba(56, 71, 109, 0.09);

  .info-btn {
    background-color: rgba(255, 255, 255, 0.12);
  }
}
</style>
